import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {PluginArguments, DynamoDataForExtension} from '../../../types';
import {getCollectionSlugById} from '@packages/systems/dynamo/utils/CollectionUtils';
import {
  fieldSlug,
  collectionSlug,
} from '@packages/systems/dynamo/utils/SlugUtils';
import {
  getDynamoItemElement,
  getCollectionIdFromElement,
  getCollectionItemRefSetSlugFromElement,
  getContextCollectionIdFromElement,
  isPaginatedDynamoWrapperElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

export const getDataExtension = (
  element: ExpressionElement,
  dynamoData: DynamoDataForExtension,
  pluginArguments: PluginArguments
) => {
  const {collections} = pluginArguments;
  const dataPath = dynamoData.getDataPathForElement(element);
  const data = dynamoData.getDataForElement(element);
  // TODO (roope) Stop relying on `list_` alias as it is very fragile. Perhaps
  //      we should not have special treatment for nested lists. Using this for
  //      now as passing `tree` breaks memoization.
  const isNestedDynamoWrapper = dataPath.some((fieldName) =>
    /^list_/.test(fieldName)
  );

  if (isNestedDynamoWrapper) {
    return data;
  }

  const dynamoItem = getDynamoItemElement(element);

  if (!dynamoItem) {
    return data;
  }

  const hasContextItem = dataPath.length > 0;

  const contextItemData = hasContextItem
    ? {
        bindingCollection: {
          item: data,
        },
      }
    : {};

  const listCollectionId = getCollectionIdFromElement(element);

  if (!listCollectionId) {
    return hasContextItem
      ? {
          database: {
            collections: {
              ...contextItemData,
            },
          },
        }
      : data;
  }

  const listItems = dynamoData.getDataForElement(dynamoItem) || [];

  const itemRefSet = getCollectionItemRefSetSlugFromElement(element);
  const contextCollectionId = getContextCollectionIdFromElement(element);
  const contextCollectionSlug = contextCollectionId
    ? getCollectionSlugById(contextCollectionId, collections)
    : null;

  if (itemRefSet && contextCollectionSlug) {
    return {
      database: {
        collections: {
          [collectionSlug(contextCollectionSlug)]: {
            item: {
              [fieldSlug(itemRefSet)]: listItems,
            },
          },
          ...contextItemData,
        },
      },
    };
  }

  const paginationEnabled = isPaginatedDynamoWrapperElement(element);
  const listCollectionSlug = collectionSlug(
    getCollectionSlugById(listCollectionId, collections)
  );

  // We retrieve the count relative to the list items path, because the
  // list items path encapsulates the aliasing schema, and we don't want to
  // duplicate the logic here!
  // List items are at ['cms', 'c_collection_', 'list_wrapper_id', 'edges']
  const listItemsDataPath = dynamoData.getDataPathForElement(dynamoItem);
  // List count is at ['cms', 'c_collection_', 'list_wrapper_id', 'count']
  const listCountDataPath = listItemsDataPath.slice(0, -1).concat('count');

  const listCollectionFields = paginationEnabled
    ? {
        items: listItems,
        count: dynamoData.getDataForPath(listCountDataPath),
      }
    : {items: listItems};

  return {
    database: {
      collections: {
        [listCollectionSlug]: listCollectionFields,
        ...contextItemData,
      },
    },
  };
};
